import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { ApiResponse, Filial, TabelaInfo, EstrutuaTabela } from '@/types';
import toast from 'react-hot-toast';

class ApiService {
  private api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
      timeout: 30000,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    try {
      const cfg = {
        viteMode: (import.meta as any).env?.MODE,
        viteApiBase: (import.meta as any).env?.VITE_API_BASE_URL,
        resolvedBaseURL: (this.api.defaults as any).baseURL,
        withCredentials: (this.api.defaults as any).withCredentials,
        pageOrigin: typeof window !== 'undefined' ? window.location.origin : undefined,
      };
      console.log('[FRONT][API] Configuração inicial:', cfg);
      (window as any).__API_DIAG__ = cfg;
      // Ping não bloqueante para checar conectividade
      this.api.get('/health')
        .then(r => console.log('[FRONT][API] Health OK:', r?.data))
        .catch(err => console.warn('[FRONT][API] Health FAIL:', err?.message || err));
    } catch {}

    // Interceptador para requisições
    this.api.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers = config.headers || {};
          (config.headers as any)['Authorization'] = `Bearer ${token}`;
        }
        try {
          const full = `${(this.api.defaults as any).baseURL || ''}${config.url || ''}`;
          console.log(`🚀 Requisição: ${config.method?.toUpperCase()} ${full}`);
        } catch {
          console.log(`🚀 Requisição: ${config.method?.toUpperCase()} ${config.url}`);
        }
        return config;
      },
      (error) => {
        console.error('Erro na requisição:', error);
        return Promise.reject(error);
      }
    );

    // Interceptador para respostas
    this.api.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        try {
          const success = (response?.data as any)?.success;
          console.log(`✅ Resposta: ${response.status} ${response.config.url} | success=${success}`);
        } catch {
          console.log(`✅ Resposta: ${response.status} ${response.config.url}`);
        }
        return response;
      },
      (error) => {
        console.error('Erro na resposta:', error);
        
        // Tratamento de erros
        if (error.response) {
          if (error.response.status === 401) {
            // Sessão expirada ou token inválido
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
            try {
              // Evita loop se já estiver em /login
              if (typeof window !== 'undefined' && window.location.pathname !== '/login') {
                // Mensagem amigável
                try { require('react-hot-toast').toast.error('Sessão expirada. Faça login novamente.'); } catch {}
                window.location.href = '/login';
              }
            } catch {}
          }
          const message = error.response.data?.error || 'Erro na requisição';
          toast.error(message);
        } else if (error.request) {
          toast.error('Erro de conexão com o servidor');
        } else {
          toast.error('Erro inesperado');
        }
        
        return Promise.reject(error);
      }
    );
  }

  // Preços (Admin)
  async listarProdutosEstoque(q?: string, limit: number = 500): Promise<Array<{ Controle: number; Produto: string; Venda: number }>> {
    const params: Record<string, any> = {};
    if (q) params.q = q;
    if (limit) params.limit = limit;
    const response = await this.api.get<ApiResponse<any[]>>('/precos/estoque/produtos', { params });
    return response.data.data || [];
  }

  async listarProdutosPorFilial(q?: string, grupo?: string, limit: number = 100, offset: number = 0): Promise<{
    filiais: Array<{ id: number; nome: string; alias?: string | null }>;
    produtos: Array<{ Controle: number; Produto: string; precos: Record<string, { preco: number | null; quantidade: number | null }> }>;
    hasMore?: boolean;
  }> {
    const params: Record<string, any> = {};
    if (q) params.q = q;
    if (grupo && grupo !== 'todos') params.grupo = grupo;
    if (limit) params.limit = limit;
    if (offset) params.offset = offset;
    const response = await this.api.get<ApiResponse<any>>('/precos/produtos-por-filial', { params });
    return response.data?.data || { filiais: [], produtos: [] };
  }

  async alterarPrecoTodas(produtoControle: number, novoPreco: number): Promise<void> {
    await this.api.post('/precos/alterar-todas', { produtoControle, novoPreco });
  }

  // Métodos de Autenticação
  async login(usernameOrEmail: string, password: string) {
    const response = await this.api.post('/auth/login', { usernameOrEmail, password });
    return response.data;
  }

  async me() {
    const response = await this.api.get('/auth/me');
    return response.data;
  }

  async refresh() {
    const response = await this.api.post('/auth/refresh');
    return response.data;
  }

  async logout() {
    await this.api.post('/auth/logout');
  }

  // Usuários (Admin)
  async listarUsuarios(): Promise<Array<{ id: string; name: string; username: string; email: string; is_admin: 0 | 1 | boolean; is_active: 0 | 1 | boolean; created_at: string }>> {
    const response = await this.api.get<ApiResponse<any[]>>('/users');
    return response.data.data || [];
  }

  async criarUsuario(input: { name: string; username: string; email: string; password: string; is_admin?: boolean; is_active?: boolean }): Promise<void> {
    await this.api.post('/users', input);
  }

  async atualizarUsuario(id: string | number, input: { name?: string; username?: string; email?: string; password?: string; is_admin?: boolean; is_active?: boolean }): Promise<void> {
    await this.api.put(`/users/${id}`, input);
  }

  async deletarUsuario(id: string | number): Promise<void> {
    await this.api.delete(`/users/${id}`);
  }

  // Métodos para Filiais
  async listarFiliais(): Promise<Filial[]> {
    try {
      const response = await this.api.get<ApiResponse<Filial[]>>('/filiais');
      return response.data.data || [];
    } catch (error) {
      console.error('Erro ao listar filiais:', error);
      throw error;
    }
  }

  async obterFilial(filialId: string): Promise<Filial> {
    try {
      const response = await this.api.get<ApiResponse<Filial>>(`/filiais/${filialId}`);
      if (!response.data.data) {
        throw new Error('Filial não encontrada');
      }
      return response.data.data;
    } catch (error) {
      console.error(`Erro ao obter filial ${filialId}:`, error);
      throw error;
    }
  }

  async testarConexaoFilial(filialId: string): Promise<boolean> {
    try {
      const response = await this.api.get<ApiResponse<{ conectado: boolean }>>(`/filiais/${filialId}/teste-conexao`);
      return response.data.data?.conectado || false;
    } catch (error) {
      console.error(`Erro ao testar conexão da filial ${filialId}:`, error);
      return false;
    }
  }

  // Métodos para Tabelas
  async listarTabelas(filialId: string): Promise<TabelaInfo[]> {
    try {
      const response = await this.api.get<ApiResponse<TabelaInfo[]>>(`/filiais/${filialId}/tabelas`);
      return response.data.data || [];
    } catch (error) {
      console.error(`Erro ao listar tabelas da filial ${filialId}:`, error);
      throw error;
    }
  }

  async obterEstrutuaTabela(filialId: string, nomeTabela: string): Promise<EstrutuaTabela> {
    try {
      const response = await this.api.get<ApiResponse<EstrutuaTabela>>(`/filiais/${filialId}/tabelas/${nomeTabela}/estrutura`);
      if (!response.data.data) {
        throw new Error('Estrutura da tabela não encontrada');
      }
      return response.data.data;
    } catch (error) {
      console.error(`Erro ao obter estrutura da tabela ${nomeTabela}:`, error);
      throw error;
    }
  }

  // Métodos para Relatórios (placeholders)
  async obterRelatorioVendas(filialId: string, filtros?: any): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      if (filtros) {
        Object.entries(filtros).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          const str = String(value);
          if (str.trim() === '') return;
          searchParams.append(key, str);
        });
      }
      const qs = searchParams.toString();
      const response = await this.api.get(`/relatorios/${filialId}/vendas${qs ? `?${qs}` : ''}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter relatório de vendas da filial ${filialId}:`, error);
      throw error;
    }
  }

  async obterRelatorioVendasTodas(filtros?: any): Promise<any> {
    try {
      const searchParams = new URLSearchParams();
      if (filtros) {
        Object.entries(filtros).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          const str = String(value);
          if (str.trim() === '') return;
          searchParams.append(key, str);
        });
      }
      const qs = searchParams.toString();
      const response = await this.api.get(`/relatorios/todas/vendas${qs ? `?${qs}` : ''}`);
      return response.data;
    } catch (error) {
      console.error('Erro ao obter relatório de vendas (todas as filiais):', error);
      throw error;
    }
  }

  async listarGrupos(filialId: string): Promise<Array<{ Controle: number; Grupo: string }>> {
    const response = await this.api.get(`/relatorios/${filialId}/grupos`);
    return response.data?.data || [];
  }

  async obterVendasTotalFilial(filialId: string, filtros: { dataInicio: string; dataFim: string; formaPagamento?: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/${filialId}/vendas-total?${params}`);
    return response.data;
  }

  async obterVendasTotalTodas(filtros: { dataInicio: string; dataFim: string; formaPagamento?: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/todas/vendas-total?${params}`);
    return response.data;
  }

  async obterVendasTotalGraficoTodas(filtros: { dataInicio: string; dataFim: string; formaPagamento?: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/todas/vendas-total-grafico?${params}`);
    return response.data;
  }

  async obterComissoesMecanicosTodas(filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/todas/comissoes-mecanicos?${params}`);
    return response.data;
  }

  async obterComissoesMecanicosFilial(filialId: string, filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/${filialId}/comissoes-mecanicos?${params}`);
    return response.data;
  }

  async obterRelatorioProdutos(filialId: string, filtros?: any): Promise<any> {
    try {
      const params = new URLSearchParams(filtros).toString();
      const response = await this.api.get(`/relatorios/${filialId}/produtos?${params}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter relatório de produtos da filial ${filialId}:`, error);
      throw error;
    }
  }

  async obterRelatorioClientes(filialId: string, filtros?: any): Promise<any> {
    try {
      const params = new URLSearchParams(filtros).toString();
      const response = await this.api.get(`/relatorios/${filialId}/clientes?${params}`);
      return response.data;
    } catch (error) {
      console.error(`Erro ao obter relatório de clientes da filial ${filialId}:`, error);
      throw error;
    }
  }

  async obterRelatorioMediaVendasVendedor(filialId: string, filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/${filialId}/media-vendas-vendedor?${params}`, {
      timeout: 300000
    });
    return response.data;
  }

  async obterRelatorioMediaVendasVendedorTodas(filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros).toString();
    const response = await this.api.get(`/relatorios/todas/media-vendas-vendedor?${params}`, {
      timeout: 300000
    });
    return response.data;
  }

  // Heatmap de Vendas
  async obterHeatmapVendasTodas(filtros: { dataInicio: string; dataFim: string; formaPagamento?: 'todos' | 'avista' | 'aprazo' }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/todas/heatmap-vendas?${params}`);
    return response.data;
  }

  async obterHeatmapVendasFilial(filialId: string, filtros: { dataInicio: string; dataFim: string; formaPagamento?: 'todos' | 'avista' | 'aprazo' }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/${filialId}/heatmap-vendas?${params}`);
    return response.data;
  }

  async obterTelemarketingPneusVendasTodas(filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/todas/telemarketing-pneus-vendas?${params}`, { timeout: 120000 });
    return response.data;
  }

  async obterTelemarketingPneusVendasFilial(filialId: string, filtros: { dataInicio: string; dataFim: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/${filialId}/telemarketing-pneus-vendas?${params}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusEstoqueAlertaTodas(filtros: { dias: number; minEstoque: number }): Promise<any> {
    const params = new URLSearchParams({ dias: String(filtros.dias), minEstoque: String(filtros.minEstoque) }).toString();
    const response = await this.api.get(`/relatorios/todas/pneus-estoque-alerta?${params}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusEstoqueAlertaFilial(filialId: string, filtros: { dias: number; minEstoque: number }): Promise<any> {
    const params = new URLSearchParams({ dias: String(filtros.dias), minEstoque: String(filtros.minEstoque) }).toString();
    const response = await this.api.get(`/relatorios/${filialId}/pneus-estoque-alerta?${params}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusEstoquePorAroTodas(): Promise<any> {
    const response = await this.api.get('/relatorios/todas/pneus-estoque-por-aro', { timeout: 120000 });
    return response.data;
  }

  async obterPneusEstoqueMedidasPorAroFilial(filialId: string, aro: string): Promise<any> {
    const response = await this.api.get(`/relatorios/${filialId}/pneus-estoque-por-aro/${encodeURIComponent(aro)}/medidas`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusEstoqueProdutosPorMedidaFilial(filialId: string, medidaId: string | number): Promise<any> {
    const response = await this.api.get(`/relatorios/${filialId}/pneus-estoque-medidas/${encodeURIComponent(String(medidaId))}/produtos`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusPorNcTodas(filtros: { dataInicio: string; dataFim: string; NC?: string; produto?: string; cliente?: string; grupo?: string }): Promise<any> {
    const params = new URLSearchParams();
    params.set('dataInicio', filtros.dataInicio);
    params.set('dataFim', filtros.dataFim);
    if (filtros.NC) params.set('NC', filtros.NC);
    if (filtros.produto) params.set('produto', filtros.produto);
    if (filtros.cliente) params.set('cliente', filtros.cliente);
    if (filtros.grupo) params.set('grupo', filtros.grupo);
    const response = await this.api.get(`/relatorios/todas/pneus-por-nc?${params.toString()}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusPorNcFilial(filialId: string, filtros: { dataInicio: string; dataFim: string; NC?: string; produto?: string; cliente?: string; grupo?: string }): Promise<any> {
    const params = new URLSearchParams();
    params.set('dataInicio', filtros.dataInicio);
    params.set('dataFim', filtros.dataFim);
    if (filtros.NC) params.set('NC', filtros.NC);
    if (filtros.produto) params.set('produto', filtros.produto);
    if (filtros.cliente) params.set('cliente', filtros.cliente);
    if (filtros.grupo) params.set('grupo', filtros.grupo);
    const response = await this.api.get(`/relatorios/${filialId}/pneus-por-nc?${params.toString()}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusSemObrigatoriosTodas(filtros: { dataInicio: string; dataFim: string; produto?: string; aro?: string }): Promise<any> {
    const params = new URLSearchParams();
    params.set('dataInicio', filtros.dataInicio);
    params.set('dataFim', filtros.dataFim);
    if (filtros.produto) params.set('produto', filtros.produto);
    if (filtros.aro) params.set('aro', filtros.aro);
    const response = await this.api.get(`/relatorios/todas/pneus-sem-obrigatorios?${params.toString()}`, { timeout: 120000 });
    return response.data;
  }

  async obterPneusSemObrigatoriosFilial(filialId: string, filtros: { dataInicio: string; dataFim: string; produto?: string; aro?: string }): Promise<any> {
    const params = new URLSearchParams();
    params.set('dataInicio', filtros.dataInicio);
    params.set('dataFim', filtros.dataFim);
    if (filtros.produto) params.set('produto', filtros.produto);
    if (filtros.aro) params.set('aro', filtros.aro);
    const response = await this.api.get(`/relatorios/${filialId}/pneus-sem-obrigatorios?${params.toString()}`, { timeout: 120000 });
    return response.data;
  }

  // Ponto - Irregularidades da Manhã
  async obterPontoIrregularidadesManhaTodas(filtros: { data: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/todas/ponto-irregularidades-manha?${params}`);
    return response.data;
  }

  async obterPontoIrregularidadesManhaFilial(filialId: string, filtros: { data: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/${filialId}/ponto-irregularidades-manha?${params}`);
    return response.data;
  }

  // Ponto - Esquecimento de Batida
  async obterPontoEsquecimentoTodas(filtros: { data: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/todas/ponto-esquecimento?${params}`);
    return response.data;
  }

  async obterPontoEsquecimentoFilial(filialId: string, filtros: { data: string }): Promise<any> {
    const params = new URLSearchParams(filtros as any).toString();
    const response = await this.api.get(`/relatorios/${filialId}/ponto-esquecimento?${params}`);
    return response.data;
  }

  // Ponto - Ranking de Atrasos
  private normalizeAtrasosFiltros(filtros: { dataInicio: string; dataFim: string; ordenarPor?: 'minutos' | 'quantidade'; direcao?: 'maiores' | 'menores'; somenteComAtraso?: string | number | boolean }): URLSearchParams {
    const p = new URLSearchParams();
    p.set('dataInicio', filtros.dataInicio);
    p.set('dataFim', filtros.dataFim);
    if (filtros.ordenarPor) p.set('ordenarPor', filtros.ordenarPor);
    if (filtros.direcao) p.set('direcao', filtros.direcao);
    if (typeof filtros.somenteComAtraso !== 'undefined') {
      const v = (filtros.somenteComAtraso === true || filtros.somenteComAtraso === 1 || filtros.somenteComAtraso === '1') ? '1' : '0';
      p.set('somenteComAtraso', v);
    }
    return p;
  }

  async obterPontoAtrasosTodas(filtros: { dataInicio: string; dataFim: string; ordenarPor?: 'minutos' | 'quantidade'; direcao?: 'maiores' | 'menores'; somenteComAtraso?: string | number | boolean }): Promise<any> {
    const params = this.normalizeAtrasosFiltros(filtros).toString();
    const response = await this.api.get(`/relatorios/todas/ponto-atrasos?${params}`);
    return response.data;
  }

  async obterPontoAtrasosFilial(filialId: string, filtros: { dataInicio: string; dataFim: string; ordenarPor?: 'minutos' | 'quantidade'; direcao?: 'maiores' | 'menores'; somenteComAtraso?: string | number | boolean }): Promise<any> {
    const params = this.normalizeAtrasosFiltros(filtros).toString();
    const response = await this.api.get(`/relatorios/${filialId}/ponto-atrasos?${params}`);
    return response.data;
  }

  // WhatsApp - teste (ID 1)
  async enviarWhatsTesteId1(): Promise<void> {
    await this.api.post('/whatsapp/test-id1', {});
  }

  // TextMeBot - teste (destinos fixos)
  async enviarTextMeBotTeste(): Promise<void> {
    await this.api.post('/textmebot/test', {});
  }

  async obterWhatsappProvider(): Promise<{
    provider: 'textmebot' | 'evolution';
    label: string;
    reconnectUrl: string | null;
    reconnectHint: string;
    supportsInlineQr: boolean;
  }> {
    const res = await this.api.get('/whatsapp/provider');
    return res.data?.data || {
      provider: 'textmebot',
      label: 'TextMeBot',
      reconnectUrl: null,
      reconnectHint: '',
      supportsInlineQr: false,
    };
  }

  async obterWhatsappProviderStatus(): Promise<{
    provider: 'textmebot' | 'evolution';
    state: 'open' | 'connecting' | 'close' | 'unknown';
    instance: string | null;
    supportsInlineQr: boolean;
    error?: string | null;
  }> {
    const res = await this.api.get('/whatsapp/provider/status');
    return res.data?.data || {
      provider: 'textmebot',
      state: 'unknown',
      instance: null,
      supportsInlineQr: false,
      error: null,
    };
  }

  async conectarWhatsappProvider(): Promise<{
    provider: 'textmebot' | 'evolution';
    state: 'open' | 'connecting' | 'close' | 'unknown';
    instance: string | null;
    qrBase64: string | null;
    pairingCode: string | null;
    supportsInlineQr: boolean;
  }> {
    const res = await this.api.post('/whatsapp/provider/connect', {});
    return res.data?.data || {
      provider: 'evolution',
      state: 'unknown',
      instance: null,
      qrBase64: null,
      pairingCode: null,
      supportsInlineQr: false,
    };
  }

  async desconectarWhatsappProvider(): Promise<{
    provider: 'textmebot' | 'evolution';
    state: 'open' | 'connecting' | 'close' | 'unknown';
    instance: string | null;
    supportsInlineQr: boolean;
  }> {
    const res = await this.api.post('/whatsapp/provider/disconnect', {});
    return res.data?.data || {
      provider: 'evolution',
      state: 'close',
      instance: null,
      supportsInlineQr: false,
    };
  }

  async enviarWhatsappProviderTeste(): Promise<{ provider: string }> {
    const res = await this.api.post('/whatsapp/provider/test', {});
    return { provider: res.data?.provider || '' };
  }

  // Vendedores e Holerite (assinatura)
  async listarVendedores(filialId: string): Promise<Array<{ Controle: number; Nome: string }>> {
    const res = await this.api.get(`/relatorios/${filialId}/vendedores`);
    return res.data?.data || [];
  }

  async criarAssinaturaHoleriteDb(input: {
    filialId: string;
    vendedorControle: number;
    inicio: string; // YYYY-MM-DD
    fim: string;    // YYYY-MM-DD
    signerName: string;
    signerEmail: string;
    signerPhone?: string;
  }): Promise<{ documentId: string }> {
    const res = await this.api.post('/assinaturas/autentique/holerite-db', input);
    return res.data?.data || { documentId: '' };
  }

  // Funcionários: resolver nomes (todas as filiais, ignora acentuação)
  async resolverFuncionariosPorNomes(nomes: string[]): Promise<Array<{ nomePdf: string; filialId: string | null; filialNome: string | null; controle: number | null; nomeDb: string | null; celular: string | null }>> {
    const res = await this.api.post('/funcionarios/resolver-nomes', { nomes });
    return res.data?.data || [];
  }

  async criarAssinaturaFuncionarioPdf(input: {
    mode: 'test' | 'production';
    signerName: string;
    nomeArquivo: string;
    nomeFuncionarioPdf: string;
    filialNome?: string | null;
    celularFuncionario?: string | null;
    pdfBase64: string;
    fixedTestPhone: string;
  }): Promise<{ documentId: string; signerLinks: Array<{ id: string; link: string }>; mode: 'test' | 'production'; destino: string; celularFuncionario: string | null; textMeBot: Array<{ recipient: string; ok: boolean; status: number; body: string }> }> {
    const res = await this.api.post('/assinaturas/autentique/funcionario-pdf', input, { timeout: 120000 });
    return res.data?.data;
  }

  // E-mail (teste)
  async enviarEmailTeste(): Promise<void> {
    await this.api.post('/email/test');
  }

  // Configuração de e-mail (admin)
  async listarEmailConfigs(): Promise<any[]> {
    const response = await this.api.get('/email-config');
    return response.data?.data || [];
  }

  async obterEmailConfig(description: string): Promise<any | null> {
    const response = await this.api.get(`/email-config/${encodeURIComponent(description)}`);
    return response.data?.data || null;
  }

  async salvarEmailConfig(input: any): Promise<void> {
    await this.api.post('/email-config', input);
  }

  async obterEmailConfigPorId(id: number | string): Promise<any | null> {
    const response = await this.api.get(`/email-config/id/${id}`);
    return response.data?.data || null;
  }

  async excluirEmailConfig(id: number | string): Promise<void> {
    await this.api.delete(`/email-config/${id}`);
  }

  async atualizarEmailConfig(id: number | string, input: any): Promise<void> {
    await this.api.put(`/email-config/${id}`, input);
  }

  // Schedules (Admin)
  async listarSchedules(): Promise<any[]> {
    const res = await this.api.get('/schedules');
    return res.data?.data || [];
  }

  async criarSchedule(input: any): Promise<void> {
    await this.api.post('/schedules', input);
  }

  async atualizarSchedule(id: number | string, input: any): Promise<void> {
    await this.api.put(`/schedules/${id}`, input);
  }

  async excluirSchedule(id: number | string): Promise<void> {
    await this.api.delete(`/schedules/${id}`);
  }

  async dispararScheduleAgora(id: number | string): Promise<any> {
    const res = await this.api.post(`/schedules/${id}/trigger`);
    return res.data;
  }

  // Alerta de pneus sem produtos obrigatórios (Admin)
  async listarRegrasAlertaPneus(): Promise<any[]> {
    const res = await this.api.get('/tire-alert/rules');
    return res.data?.data || [];
  }

  async criarRegraAlertaPneus(input: any): Promise<void> {
    await this.api.post('/tire-alert/rules', input);
  }

  async atualizarRegraAlertaPneus(id: number | string, input: any): Promise<void> {
    await this.api.put(`/tire-alert/rules/${id}`, input);
  }

  async excluirRegraAlertaPneus(id: number | string): Promise<void> {
    await this.api.delete(`/tire-alert/rules/${id}`);
  }

  async buscarProdutosDepositoAlertaPneus(q: string, limit: number = 30): Promise<any[]> {
    const res = await this.api.get('/tire-alert/products', { params: { q, limit } });
    return res.data?.data || [];
  }

  async previewAlertaPneus(windowMinutes: number = 15): Promise<any> {
    const res = await this.api.post('/tire-alert/preview', { windowMinutes });
    return res.data?.data || { alerts: [], errors: [], windowMinutes };
  }

  // CallMeBot (Admin)
  async listarCallMeBotRecipients(): Promise<Array<{ id: number; label: string | null; phone: string; active: 0 | 1 | boolean; created_at: string; updated_at: string }>> {
    const res = await this.api.get('/whatsapp/recipients');
    return res.data?.data || [];
  }

  async obterCallMeBotRecipient(id: number | string): Promise<{ id: number; label: string | null; phone: string; active: 0 | 1 | boolean } | null> {
    const res = await this.api.get(`/whatsapp/recipients/${id}`);
    return res.data?.data || null;
  }

  async criarCallMeBotRecipient(input: { label?: string; phone: string; active?: boolean }): Promise<void> {
    await this.api.post('/whatsapp/recipients', input);
  }

  async atualizarCallMeBotRecipient(id: number | string, input: { label?: string; phone: string; active?: boolean }): Promise<void> {
    await this.api.put(`/whatsapp/recipients/${id}`, input);
  }

  async excluirCallMeBotRecipient(id: number | string): Promise<void> {
    await this.api.delete(`/whatsapp/recipients/${id}`);
  }

  // Ponto - Horários e Escalas por filial (Admin)
  async listarPontoHorarios(filialId: string, somenteAtivos: boolean = false): Promise<any[]> {
    const res = await this.api.get('/ponto/horarios', { params: { filialId, somenteAtivos: somenteAtivos ? '1' : '0' } });
    return res.data?.data || [];
  }

  async obterPontoHorario(filialId: string, controle: number | string): Promise<any | null> {
    const res = await this.api.get(`/ponto/horarios/${controle}`, { params: { filialId } });
    return res.data?.data || null;
  }

  async criarPontoHorario(input: any): Promise<void> {
    await this.api.post('/ponto/horarios', input);
  }

  async atualizarPontoHorario(filialId: string, controle: number | string, input: any): Promise<void> {
    await this.api.put(`/ponto/horarios/${controle}`, { filialId, ...input });
  }

  async excluirPontoHorario(filialId: string, controle: number | string): Promise<void> {
    await this.api.delete(`/ponto/horarios/${controle}`, { params: { filialId } });
  }

  async listarPontoEscalas(filialId: string, somenteAtivos: boolean = false): Promise<any[]> {
    const res = await this.api.get('/ponto/escalas', { params: { filialId, somenteAtivos: somenteAtivos ? '1' : '0' } });
    return res.data?.data || [];
  }

  async obterPontoEscala(filialId: string, controle: number | string): Promise<any | null> {
    const res = await this.api.get(`/ponto/escalas/${controle}`, { params: { filialId } });
    return res.data?.data || null;
  }

  async criarPontoEscala(input: any): Promise<void> {
    await this.api.post('/ponto/escalas', input);
  }

  async atualizarPontoEscala(filialId: string, controle: number | string, input: any): Promise<void> {
    await this.api.put(`/ponto/escalas/${controle}`, { filialId, ...input });
  }

  async excluirPontoEscala(filialId: string, controle: number | string): Promise<void> {
    await this.api.delete(`/ponto/escalas/${controle}`, { params: { filialId } });
  }

  async listarPontoFuncionarios(filialId: string, somenteAtivos: boolean = true): Promise<any[]> {
    const res = await this.api.get('/ponto/funcionarios', { params: { filialId, somenteAtivos: somenteAtivos ? '1' : '0' } });
    return res.data?.data || [];
  }

  async atualizarEscalaFuncionario(filialId: string, funcionarioControle: number | string, escala: string): Promise<void> {
    await this.api.put(`/ponto/funcionarios/${funcionarioControle}/escala`, { filialId, Escala: escala });
  }

  // Método para health check
  async healthCheck(): Promise<boolean> {
    try {
      const response = await this.api.get('/health');
      return response.data.success;
    } catch (error) {
      console.error('Erro no health check:', error);
      return false;
    }
  }
}

// Instância singleton
export const apiService = new ApiService();
export default apiService;